import { Button } from '@/components/common/Button';
import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import {
    EntityViewContent,
    entityViewInset,
} from '@/components/common/EntityView';
import FadeEdge from '@/components/common/FadeEdge';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexRow } from '@/components/common/Flex';
import { ArrowLeft } from '@/components/common/icons';
import { FlexProps } from '@chakra-ui/react';
import { useGoalOnboardingContext } from './GoalOnboardingContext';

export interface GoalOnboardingLayoutProps extends FlexProps {
    footer?: {
        cta?: {
            onComplete: VoidFunction;
            text?: string;
            isSubmitting?: boolean;
            isDisabled?: boolean;
        };
        secondary?: {
            onClick: VoidFunction;
            text: string;
        };
        back?: {
            onBack: VoidFunction;
            text?: string;
        };
    };
}

const GoalOnboardingLayout = ({
    children,
    footer,
    ...props
}: GoalOnboardingLayoutProps) => {
    const { step, goal } = useGoalOnboardingContext();
    const backLabel = footer?.back?.text ?? 'Back';
    const continueLabel = footer?.cta?.text ?? 'Continue';

    return (
        <>
            <EntityViewContent
                px={entityViewInset}
                pb={40}
                justify="center"
                {...props}
            >
                {children}
            </EntityViewContent>
            {footer && (
                <FlexRow
                    grow={0}
                    shrink={0}
                    px={entityViewInset}
                    pb={{ base: 10, '2xl': 16 }}
                    pt={{ base: 6, '2xl': 10 }}
                    justify="space-between"
                    pos="fixed"
                    bottom={0}
                    width="full"
                    bg="white"
                    zIndex={290}
                    overflow="hidden"
                >
                    <FadeInBox>
                        {footer.back && (
                            <ButtonWithIcon
                                variant="unstyled"
                                iconLeft={<ArrowLeft size="sm" />}
                                color="gray.500"
                                onClick={footer.back.onBack}
                                onClickEvent={
                                    'Goal Onboarding Back Button Clicked'
                                }
                                onClickEventParams={{
                                    label: backLabel,
                                    step,
                                    goal_type: goal.type,
                                }}
                                mr={16}
                            >
                                {backLabel}
                            </ButtonWithIcon>
                        )}
                    </FadeInBox>
                    <FlexRow align="center" gap={4}>
                        {footer.secondary && (
                            <FadeInBox>
                                <Button
                                    variant="ghost"
                                    size="lg"
                                    colorScheme="gray"
                                    onClick={footer.secondary.onClick}
                                    onClickEvent={
                                        'Goal Onboarding Secondary Button Clicked'
                                    }
                                    onClickEventParams={{
                                        label: footer.secondary.text,
                                        step,
                                        goal_type: goal.type,
                                    }}
                                >
                                    {footer.secondary.text}
                                </Button>
                            </FadeInBox>
                        )}
                        {footer.cta && (
                            <Button
                                onClick={footer.cta.onComplete}
                                onClickEvent={
                                    'Goal Onboarding Continue Button Clicked'
                                }
                                onClickEventParams={{
                                    label: continueLabel,
                                    step,
                                    goal_type: goal.type,
                                }}
                                isLoading={footer.cta.isSubmitting}
                                disabled={
                                    footer.cta.isDisabled ??
                                    footer.cta.isSubmitting
                                }
                            >
                                {continueLabel}
                            </Button>
                        )}
                    </FlexRow>
                    <FadeEdge direction="up" top={0} left={0} w="full" />
                </FlexRow>
            )}
        </>
    );
};

export default GoalOnboardingLayout;
